import React from "react";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { withDefault } from "../utils/withDefault";
import { AppModalAction, AppModalProps } from "./Generic/AppModal/AppModal";

const initialModalState: AppModalProps = {
  type: "info",
  isOpen: false,
  title: "",
  actions: [],
};

const useModal = () => {
  const [modalState, setModalState] = React.useState<AppModalProps>(initialModalState);

  function setModalAsClosed() {
    return setModalState((prevState) => ({ ...prevState, isOpen: false }));
  }

  function setModalAsLoading() {
    setModalState((prevState) => ({ ...prevState, isLoading: true, isOpen: true }));
  }

  function setModalAsError(params: { title: string; message: string }) {
    return setModalState({
      type: "error",
      title: params.title,
      message: params.message,
      isLoading: false,
      isOpen: true,
      onRequestClose: setModalAsClosed,
      actions: [
        {
          key: "close",
          type: "button",
          children: translate(STRINGS.CLOSE),
          onClick: setModalAsClosed,
          variant: "soft",
          color: "danger",
        },
      ],
    });
  }

  const setModalAsSuccess = (
    params:
      | {
          title: string;
          message?: string;
          closeText: string;
          onRequestClose?: () => void;
        }
      | {
          title: string;
          message?: string;
          actions: AppModalAction[];
          onRequestClose?: () => void;
        }
  ) => {
    return setModalState({
      type: "success",
      title: params.title,
      message: params.message,
      isLoading: false,
      isOpen: true,
      onRequestClose: params.onRequestClose ?? setModalAsClosed,
      actions:
        "actions" in params
          ? params.actions
          : [
              {
                key: "close",
                type: "button",
                children: params.closeText,
                variant: "soft",
                color: "success",
                onClick: params.onRequestClose ?? setModalAsClosed,
              },
            ],
    });
  };

  const setModalAsPrompt = (params: {
    title: string;
    message: string;
    actions: AppModalAction[];
    closeText?: string;
    icon?: React.ReactNode;
    withoutClose?: boolean;
  }) => {
    return setModalState({
      type: "info",
      title: params.title,
      message: params.message,
      isLoading: false,
      isOpen: true,
      icon: params.icon,
      onRequestClose: setModalAsClosed,
      actions:
        params.withoutClose === true
          ? params.actions
          : [
              ...params.actions,
              {
                key: "close",
                type: "button",
                children: withDefault(params.closeText, translate(STRINGS.CLOSE)),
                onClick: setModalAsClosed,
                variant: "link",
                color: "black",
              },
            ],
    });
  };

  const modalProps = {
    ...modalState,
    onRequestClose: setModalAsClosed,
  };

  return {
    modalProps: modalProps,
    setModalAsLoading: setModalAsLoading,
    setModalAsError: setModalAsError,
    setModalAsSuccess: setModalAsSuccess,
    setModalAsClosed: setModalAsClosed,
    setModalAsPrompt: setModalAsPrompt,
    open: () => setModalState((prevState) => ({ ...prevState, isOpen: true })),
    close: () => setModalState((prevState) => ({ ...prevState, isOpen: false })),
  };
};

export default useModal;
